// src/pages/Lounge/MemberClubsFeed/MemberClubsFeed.jsx
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import TextPostCard from "@/components/Post/TextPostCard/TextPostCard";
import PhotoPostCard from "@/components/Post/PhotoPostCard/PhotoPostCard";
import VideoPostCard from "@/components/Post/VideoPostCard/VideoPostCard";
import Loading from "@/components/Loading/Loading";

import api from "@/utils/axios";

import cls from "./MemberClubsFeed.module.scss";

const FEED_LIMIT = 15;

const MemberClubsFeed = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const isPublic = location.pathname === "/lounge";
  const isFriends = location.pathname === "/lounge/friends-feed";
  const isClubs = location.pathname === "/lounge/clubs-feed";
  const isChat = location.pathname === "/lounge/chat";
  const isSearch = location.pathname === "/lounge/search";

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [feedErr, setFeedErr] = useState("");
  const [posts, setPosts] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [nextPage, setNextPage] = useState(2);
  const loadMoreRef = useRef(null);
  const pagingRequestRef = useRef(false);

  const loadFeed = async ({ page = 1, append = false } = {}) => {
    if (append && pagingRequestRef.current) return;
    if (append) pagingRequestRef.current = true;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    setFeedErr("");
    try {
      const res = await api.get("/v1/posts/feed/clubs", {
        params: { page, limit: FEED_LIMIT },
      });

      const rows =
        res?.data?.rows ||
        res?.data?.posts ||
        res?.data?.data ||
        res?.data?.items ||
        [];

      const normalizedRows = Array.isArray(rows) ? rows : [];
      const hasMoreFromApi = Boolean(res?.data?.meta?.hasMore);

      setPosts((prev) =>
        append ? [...prev, ...normalizedRows] : normalizedRows,
      );
      setHasMore(hasMoreFromApi || normalizedRows.length === FEED_LIMIT);
      setNextPage(page + 1);
    } catch (e) {
      setFeedErr(
        e?.response?.data?.message || e?.message || "Failed to load feed",
      );
      if (!append) {
        setPosts([]);
      }
    } finally {
      if (append) pagingRequestRef.current = false;
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadFeed({ page: 1, append: false });
  }, []);

  useEffect(() => {
    if (!hasMore || loading || loadingMore) return undefined;
    if (typeof window === "undefined") return undefined;
    if (!("IntersectionObserver" in window)) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || pagingRequestRef.current) return;
        loadFeed({ page: nextPage, append: true });
      },
      { rootMargin: "200px 0px" },
    );

    const el = loadMoreRef.current;
    if (el) observer.observe(el);

    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, nextPage]);

  const safeStr = (v) => (v == null ? "" : String(v));

  const getVideoIdFromPost = (post) => {
    const v = post?.videoId;
    if (!v) return "";
    if (typeof v === "string") return v;
    if (typeof v === "object" && v?._id) return v._id;
    return "";
  };

  const renderFeedPost = (post) => {
    const type = safeStr(post?.type).toLowerCase();
    const hasImages = Array.isArray(post?.images) && post.images.length > 0;
    const hasVideo = !!getVideoIdFromPost(post);

    if (type === "video-post" || type === "video")
      return <VideoPostCard post={post} />;
    if (type === "photo-post" || type === "photo")
      return <PhotoPostCard post={post} />;
    if (type === "text-only" || type === "text")
      return <TextPostCard post={post} />;

    if (hasVideo) return <VideoPostCard post={post} />;
    if (hasImages) return <PhotoPostCard post={post} />;
    return <TextPostCard post={post} />;
  };

  if (loading) {
    return (
      <Loading
        fullscreen
        titleKey="loading.title"
        subtitleKey="loading.subtitle"
        showTips
        cycleMs={1400}
      />
    );
  }

  return (
    <div className={cls.page}>
      <div className={cls.pageInner}>
        {/* Tabs row */}
        <nav
          className={cls.tabs}
          aria-label={t("memberPublicFeed.tabs.aria", "Lounge feed tabs")}
        >
          <Link
            to="/lounge"
            className={`${cls.tabButton} ${isPublic ? cls.activeTab : ""}`}
            aria-current={isPublic ? "page" : undefined}
          >
            {t("memberPublicFeed.tabs.public")}
          </Link>
          <Link
            to="/lounge/friends-feed"
            className={`${cls.tabButton} ${isFriends ? cls.activeTab : ""}`}
            aria-current={isFriends ? "page" : undefined}
          >
            {t("memberPublicFeed.tabs.friends")}
          </Link>
          <Link
            to="/lounge/clubs-feed"
            className={`${cls.tabButton} ${isClubs ? cls.activeTab : ""}`}
            aria-current={isClubs ? "page" : undefined}
          >
            {t("memberPublicFeed.tabs.clubs")}
          </Link>
          <Link
            to="/lounge/chat"
            className={`${cls.tabButton} ${isChat ? cls.activeTab : ""}`}
            aria-current={isChat ? "page" : undefined}
          >
            {t("memberPublicFeed.tabs.chat")}
          </Link>
          <Link
            to="/lounge/search"
            className={`${cls.tabButton} ${isSearch ? cls.activeTab : ""}`}
            aria-current={isSearch ? "page" : undefined}
          >
            🔍 {t("memberPublicFeed.tabs.search")}
          </Link>
        </nav>

        {/* Main layout – single centered column */}
        <div className={cls.layout}>
          <div className={cls.rightColumn}>
            {/* Clubs CTA – compact 3-button row */}
            <section className={cls.card}>
              <div className={cls.cardHeaderRow}>
                <h2 className={cls.cardTitle}>{t("memberClubsFeed.title")}</h2>
              </div>

              <div className={cls.clubActionsRow}>
                {/* My Clubs */}
                <Link
                  to="/lounge/my-clubs"
                  className={`${cls.clubMiniBtn} ${cls.btnMyClubs}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path
                        d="M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 
          1.34-3 3 1.34 3 3 3zM8 11c1.66 0 3-1.34 
          3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 
          2c-2.33 0-7 1.17-7 3.5V20h14v-3.5C15 
          14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 
          1.16.84 1.97 1.97 1.97 3.45V20h6v-3.5c0-2.33-4.67-3.5-7-3.5z"
                      />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.myClubs.label")}
                </Link>

                {/* Explore */}
                <Link
                  to="/lounge/explore-universe"
                  className={`${cls.clubMiniBtn} ${cls.btnExplore}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path
                        d="M12 2C6.48 2 2 6.48 2 
          12s4.48 10 10 10 10-4.48 
          10-10S17.52 2 12 
          2zm4.19 6.19l-2.47 7.4c-.1.31-.49.31-.59 
          0l-2.47-7.4a.5.5 0 01.63-.63l7.4 2.47c.31.1.31.49 
          0 .59l-7.4 2.47a.5.5 0 01-.63-.63l2.47-7.4c.1-.31.49-.31.59 
          0l2.47 7.4a.5.5 0 01-.63.63l-7.4-2.47c-.31-.1-.31-.49 
          0-.59l7.4-2.47a.5.5 0 01.63.63z"
                      />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.exploreUniverse.label")}
                </Link>

                {/* Search */}
                <Link
                  to="/lounge/search-clubs"
                  className={`${cls.clubMiniBtn} ${cls.btnSearch}`}
                >
                  <span className={cls.icon}>
                    <svg viewBox="0 0 24 24">
                      <path
                        d="M15.5 14h-.79l-.28-.27A6.471 
          6.471 0 0016 9.5 6.5 6.5 
          0 109.5 16a6.471 6.471 0 004.23-1.57l.27.28v.79l5 
          5L20.49 19l-5-5zm-6 
          0C7.01 14 5 11.99 5 9.5S7.01 
          5 9.5 5 14 7.01 14 9.5 11.99 
          14 9.5 14z"
                      />
                    </svg>
                  </span>
                  {t("memberClubsFeed.cta.searchClubs.label")}
                </Link>
              </div>
            </section>

            <section className={cls.feedWrap}>
              {feedErr ? (
                <div className={cls.feedStateBad}>{feedErr}</div>
              ) : posts.length === 0 ? (
                <div className={cls.feedState}>
                  {t("memberPublicFeed.feed.empty")}
                </div>
              ) : (
                <div className={cls.postList}>
                  {posts.map((post) => (
                    <React.Fragment key={post._id}>
                      {renderFeedPost(post)}
                    </React.Fragment>
                  ))}
                  {hasMore ? (
                    <div ref={loadMoreRef} className={cls.feedLoadState}>
                      {loadingMore
                        ? "Loading more posts..."
                        : "Scroll to load more posts"}
                    </div>
                  ) : (
                    <div className={cls.feedLoadState}>No more posts</div>
                  )}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberClubsFeed;
